// -------------------------------------------------------------------------
// Renders a single row within the admin data table.
//
// Handles:
// - Displaying row values
// - Editing normal fields
// - Editing foreign-key fields using dropdowns
// - Editing boolean fields using checkboxes
// - Saving / cancelling edits
// - Deleting rows
// -------------------------------------------------------------------------

import React from 'react';

const TableRow = ({
    // Row data and schema
    row,
    schema,
    primaryKey,

    // Foreign-key dropdown options
    dropdownOptions,

    // Editing state
    isEditing,
    editingValues,
    onChangeEditingValue,

    // Table permissions
    editable,
    allowDelete,

    // Row actions
    onStartEdit,
    onSaveEdit,
    onCancelEdit,
    onDelete,
}) => {

    // ---------------------------------------------------------------------
    // Display Helpers
    // ---------------------------------------------------------------------

    /**
     * Converts a stored foreign-key ID into its human-readable label.
     *
     * Foreign-key options are loaded by AdminTablesContainer and passed
     * down through DataTable.
     */
    const getDisplayValue = (column, value) => {

        if (!column.foreignKey) {
            return value;
        }

        const options =
            dropdownOptions?.[column.column_name] || [];

        const option = options.find(
            option =>
                String(option.value) === String(value)
        );

        return option ? option.label : value;
    };


    /**
     * Normalizes boolean values received from the database.
     *
     * Depending on the database/API response, booleans may arrive as
     * actual JavaScript booleans or as the strings "true"/"false".
     */
    const getBooleanValue = (value) => {

        if (value === true || value === "true") {
            return true;
        }

        if (value === false || value === "false") {
            return false;
        }

        return false;
    };


    // ---------------------------------------------------------------------
    // Render
    // ---------------------------------------------------------------------

    return (
        <tr className={
            isEditing
                ? 'adtab-row adtab-row--editing'
                : 'adtab-row'
        }>

            {schema.map(column => {

                // Determine how this column should behave.
                const isPrimaryKey =
                    column.column_name === primaryKey;

                const isForeignKey =
                    !!column.foreignKey;

                const isBoolean =
                    column.data_type === "boolean" ||
                    column.postgresType === "bool";

                const options =
                    dropdownOptions?.[column.column_name] || [];


                return (
                    <td
                        key={column.column_name}
                        className="adtab-cell"
                    >

                        {isEditing && !isPrimaryKey ? (

                            // -------------------------------------------------
                            // Foreign-key field
                            // -------------------------------------------------
                            isForeignKey ? (

                                <select
                                    className="adtab-input adtab-input--cell"
                                    value={
                                        editingValues[column.column_name] ?? ""
                                    }
                                    onChange={e =>
                                        onChangeEditingValue(
                                            column.column_name,
                                            e.target.value
                                        )
                                    }
                                >

                                    <option value="">
                                        -- Select --
                                    </option>

                                    {options.map(option => (
                                        <option
                                            key={option.value}
                                            value={option.value}
                                        >
                                            {option.label}
                                        </option>
                                    ))}

                                </select>

                            ) :

                            // -------------------------------------------------
                            // Boolean field
                            // -------------------------------------------------
                            isBoolean ? (

                                <label className="adtab-checkbox">

                                    <input
                                        type="checkbox"
                                        checked={getBooleanValue(
                                            editingValues[column.column_name]
                                        )}
                                        onChange={e =>
                                            onChangeEditingValue(
                                                column.column_name,
                                                e.target.checked
                                            )
                                        }
                                    />

                                    <span>
                                        {getBooleanValue(
                                            editingValues[column.column_name]
                                        )
                                            ? "True"
                                            : "False"
                                        }
                                    </span>

                                </label>

                            ) :

                            // -------------------------------------------------
                            // Normal editable field
                            // -------------------------------------------------
                            (

                                <input
                                    className="adtab-input adtab-input--cell"
                                    value={
                                        editingValues[column.column_name] ?? ''
                                    }
                                    onChange={e =>
                                        onChangeEditingValue(
                                            column.column_name,
                                            e.target.value
                                        )
                                    }
                                />

                            )

                        ) : (

                            // -------------------------------------------------
                            // Normal display mode
                            // -------------------------------------------------
                            <span
                                className={
                                    isPrimaryKey
                                        ? 'adtab-cell__value adtab-cell__value--key'
                                        : 'adtab-cell__value'
                                }
                            >
                                {String(
                                    getDisplayValue(
                                        column,
                                        row[column.column_name]
                                    )
                                )}
                            </span>

                        )}

                    </td>
                );
            })}


            {/* -----------------------------------------------------------------
             * Row Actions
             * ----------------------------------------------------------------- */}

            <td className="adtab-cell adtab-cell--actions">

                {editable && (

                    isEditing ? (

                        <>
                            {/* Save current changes */}
                            <button
                                className="adtab-btn adtab-btn--small adtab-btn--primary"
                                onClick={onSaveEdit}
                            >
                                Save
                            </button>

                            {/* Discard current changes */}
                            <button
                                className="adtab-btn adtab-btn--small"
                                onClick={onCancelEdit}
                            >
                                Cancel
                            </button>
                        </>

                    ) : (

                        /* Start editing this row */
                        <button
                            className="adtab-btn adtab-btn--small"
                            onClick={() => onStartEdit(row)}
                        >
                            Edit
                        </button>

                    )

                )}

                {allowDelete && (

                    /* Delete this row */
                    <button
                        className="adtab-btn adtab-btn--small adtab-btn--danger"
                        onClick={() => onDelete(row)}
                    >
                        Delete
                    </button>

                )}

            </td>

        </tr>
    );
};


export default TableRow;